import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import { Link } from "react-router-dom";
import MenuBar from "../../components/MenuBar";
import HeaderBar from "../../components/HeaderBar";
import CctvPlayer from "../../components/cctv/CctvPlayer";
import CctvPtzPad from "../../components/cctv/CctvPtzPad";
import { hasPermission } from "../../config/permissions";
import {
  listCctvCameras,
  startCctvStream,
  stopCctvStream,
  cctvPtzControl,
  captureCctvSnapshot,
} from "../../services/cctvApi";
import { buildVlcTestUrl } from "../../utils/cctvRtspUrl";

const GRID_OPTS = [1, 2, 4, 6, 9];

function formatCallableError(err) {
  const code = String(err?.code || "").replace(/^functions\//, "");
  const msg = String(err?.message || err?.details || "Request failed").trim();
  if (code && code !== "internal") return `${msg} (${code})`;
  if (code === "internal") {
    if (msg && msg.length > 3 && !/^internal$/i.test(msg)) return `${msg} (${code})`;
    return "Server error (internal). Redeploy latest functions; check Firebase logs for startCctvStream / listCctvCameras.";
  }
  return msg;
}

function storedUser() {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}") || {};
  } catch {
    return {};
  }
}

export default function CctvLivePage() {
  const user = storedUser();
  const canAdmin = hasPermission(user, "cctv_admin");

  const [cameras, setCameras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gridSize, setGridSize] = useState(4);
  const [streams, setStreams] = useState({});
  const [focusId, setFocusId] = useState(null);
  const [fullscreenId, setFullscreenId] = useState(null);
  const sessionsRef = useRef({});

  const visibleCameras = useMemo(() => cameras.slice(0, gridSize), [cameras, gridSize]);
  const visibleKey = visibleCameras.map((c) => c.id).join("|");

  const loadCameras = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listCctvCameras();
      setCameras(res?.data?.cameras || []);
    } catch (e) {
      console.error("listCctvCameras", e?.code, e?.message, e);
      toast.error(formatCallableError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCameras();
  }, [loadCameras]);

  const stopOne = useCallback(async (cameraId) => {
    const sess = sessionsRef.current[cameraId];
    if (!sess) return;
    try {
      await stopCctvStream({ cameraId, sessionId: sess.sessionId });
    } catch {
      /* ignore */
    }
    delete sessionsRef.current[cameraId];
    setStreams((prev) => {
      const next = { ...prev };
      delete next[cameraId];
      return next;
    });
  }, []);

  const startOne = useCallback(
    async (camera) => {
      if (!camera?.id) return;
      await stopOne(camera.id);
      try {
        const res = await startCctvStream({ cameraId: camera.id, protocol: "hls" });
        const data = res?.data || {};
        if (!data.hlsUrl) {
          toast.error(`No HLS URL for ${camera.name || camera.id}. Check gateway settings.`);
          return;
        }
        sessionsRef.current[camera.id] = { sessionId: data.sessionId, hlsUrl: data.hlsUrl };
        setStreams((prev) => ({
          ...prev,
          [camera.id]: { hlsUrl: data.hlsUrl, name: camera.name || camera.id },
        }));
      } catch (e) {
        console.error("startCctvStream", e);
        const msg = formatCallableError(e);
        toast.error(msg, { duration: 10000 });
        if (/timed out|camera video/i.test(msg)) {
          const d = e?.details || {};
          const vlc =
            camera.rtspUrlHint ||
            buildVlcTestUrl({
              ipAddress: d.ipAddress || camera.ipAddress,
              username: d.username || camera.username,
              rtspPort: d.rtspPort || 554,
              vendor: d.vendor || camera.vendor,
              channel: d.channel ?? camera.channel,
              subtype: d.subtype ?? camera.subtype,
            });
          if (vlc) {
            toast(
              () => (
                <span>
                  Saved URL (test in VLC on gateway PC; close VLC before Live):{" "}
                  <code className="block break-all text-xs">{vlc}</code>
                  <Link className="mt-1 block text-sky-300 underline" to="/cctv/cameras">
                    Cameras → paste working URL
                  </Link>
                </span>
              ),
              { duration: 15000 }
            );
          }
        }
      }
    },
    [stopOne]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      for (const cam of visibleCameras) {
        if (cancelled) break;
        if (!sessionsRef.current[cam.id]) await startOne(cam);
      }
      const visibleIds = new Set(visibleCameras.map((c) => c.id));
      for (const id of Object.keys(sessionsRef.current)) {
        if (!visibleIds.has(id)) await stopOne(id);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [visibleKey, gridSize, startOne, stopOne]);

  useEffect(() => {
    const sessions = sessionsRef.current;
    return () => {
      Object.entries(sessions).forEach(([id, sess]) => {
        stopCctvStream({ cameraId: id, sessionId: sess?.sessionId }).catch(() => {});
      });
    };
  }, []);

  const gridClass =
    gridSize === 1
      ? "grid-cols-1"
      : gridSize === 2
        ? "grid-cols-1 md:grid-cols-2"
        : gridSize === 4
          ? "grid-cols-2"
          : gridSize === 6
            ? "grid-cols-2 lg:grid-cols-3"
            : "grid-cols-3";

  const handlePtz = async (action) => {
    if (!focusId) {
      toast.error("Select a camera tile first");
      return;
    }
    try {
      await cctvPtzControl({ cameraId: focusId, action, speed: 1 });
    } catch (e) {
      toast.error(e?.message || "PTZ failed");
    }
  };

  const handleSnapshot = async () => {
    if (!focusId) {
      toast.error("Select a camera tile first");
      return;
    }
    try {
      const res = await captureCctvSnapshot({ cameraId: focusId });
      const url = res?.data?.url || res?.data?.downloadUrl;
      if (url) window.open(url, "_blank", "noopener,noreferrer");
      else toast.success(res?.data?.message || "Snapshot requested");
    } catch (e) {
      toast.error(e?.message || "Snapshot failed");
    }
  };

  const focusCam = cameras.find((c) => c.id === focusId);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <Toaster position="top-right" />
      <MenuBar />
      <HeaderBar title="CCTV — Live" />

      <div className="mx-auto max-w-[1600px] px-3 py-3">
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-slate-800 bg-slate-900/80 px-3 py-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Layout</span>
          {GRID_OPTS.map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => setGridSize(n)}
              className={`rounded px-2.5 py-1 text-xs font-bold ${
                gridSize === n ? "bg-red-600 text-white" : "bg-slate-800 text-slate-200 hover:bg-slate-700"
              }`}
            >
              {n}
            </button>
          ))}
          <button
            type="button"
            onClick={loadCameras}
            className="ml-auto rounded bg-slate-800 px-3 py-1 text-xs font-semibold hover:bg-slate-700"
          >
            Refresh list
          </button>
          {canAdmin && (
            <>
              <Link to="/cctv/cameras" className="rounded bg-slate-800 px-3 py-1 text-xs font-semibold hover:bg-slate-700">
                Manage cameras
              </Link>
              <Link to="/tools/cctv-settings" className="rounded bg-slate-800 px-3 py-1 text-xs font-semibold hover:bg-slate-700">
                Settings
              </Link>
            </>
          )}
        </div>

        <div className="flex flex-col gap-3 lg:flex-row">
          <div className={`grid flex-1 gap-2 ${gridClass}`} style={{ minHeight: "60vh" }}>
            {loading && <p className="col-span-full text-sm text-slate-400">Loading cameras…</p>}
            {!loading && visibleCameras.length === 0 && (
              <p className="col-span-full text-sm text-slate-400">
                No cameras configured. {canAdmin ? "Add cameras under Manage cameras." : "Ask an administrator."}
              </p>
            )}
            {visibleCameras.map((cam) => {
              const st = streams[cam.id];
              const isFocus = focusId === cam.id;
              return (
                <div
                  key={cam.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => setFocusId(cam.id)}
                  onKeyDown={(e) => e.key === "Enter" && setFocusId(cam.id)}
                  className={`relative flex min-h-[180px] flex-col overflow-hidden rounded-lg border-2 ${
                    isFocus ? "border-red-500" : "border-slate-800"
                  } bg-black`}
                >
                  {st?.hlsUrl ? (
                    <CctvPlayer
                      hlsUrl={st.hlsUrl}
                      title={st.name}
                      className="min-h-[180px] flex-1"
                      onError={(err) => {
                        const msg = err?.message || "No video — enable RTSP on camera and test URL in VLC, then set Full RTSP URL in Cameras.";
                        toast.error(msg);
                      }}
                    />
                  ) : (
                    <div className="flex flex-1 items-center justify-center text-xs text-slate-500">Connecting…</div>
                  )}
                  <div className="flex items-center justify-between gap-2 border-t border-slate-800 bg-slate-900/90 px-2 py-1">
                    <span className="truncate text-[11px] text-slate-300">{cam.name}</span>
                    <div className="flex gap-1">
                      <button
                        type="button"
                        className="rounded bg-slate-800 px-2 py-0.5 text-[10px] hover:bg-slate-700"
                        onClick={(e) => {
                          e.stopPropagation();
                          startOne(cam);
                        }}
                      >
                        Reconnect
                      </button>
                      <button
                        type="button"
                        className="rounded bg-slate-800 px-2 py-0.5 text-[10px] hover:bg-slate-700"
                        onClick={(e) => {
                          e.stopPropagation();
                          setFullscreenId(cam.id);
                        }}
                      >
                        Full
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <aside className="w-full shrink-0 rounded-lg border border-slate-800 bg-slate-900/80 p-3 lg:w-52">
            <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-400">Focus camera</p>
            <p className="mb-3 truncate text-sm text-white">{focusCam?.name || "—"}</p>
            <CctvPtzPad disabled={!focusCam?.ptzEnabled} onAction={handlePtz} />
            <button
              type="button"
              disabled={!focusId}
              onClick={handleSnapshot}
              className="mt-3 w-full rounded bg-red-700 px-2 py-2 text-xs font-bold text-white hover:bg-red-600 disabled:opacity-40"
            >
              Snapshot
            </button>
          </aside>
        </div>
      </div>

      {fullscreenId && streams[fullscreenId]?.hlsUrl && (
        <div className="fixed inset-0 z-50 flex flex-col bg-black">
          <div className="flex items-center justify-between border-b border-slate-800 px-3 py-2">
            <span className="text-sm font-semibold text-white">{streams[fullscreenId].name}</span>
            <button
              type="button"
              className="rounded bg-slate-800 px-3 py-1 text-xs font-semibold"
              onClick={() => setFullscreenId(null)}
            >
              Close
            </button>
          </div>
          <CctvPlayer hlsUrl={streams[fullscreenId].hlsUrl} title={streams[fullscreenId].name} className="flex-1" />
        </div>
      )}
    </div>
  );
}
